import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { TableInput, tableDef, blankRow, cleanTable, tableProblem } from "../../components/Broadcasts/Queue/TableInput";

/**
 * Documents -> Raise document (SPEC 23.4 / 23.8). Pick the employee (async search on
 * GET /broadcasts/lookups/employees?search=, 2+ characters), fill in the HR questions of the
 * current version (a table question as a table editor), Save -> POST /broadcasts/{id}/assignments
 * {user_id, form_version, values: [{question_id, value}]}. A blank optional answer is not sent.
 *
 *   <RaiseDocumentModal formVersion questions searchEmployees raise onClose onSaved />
 *
 * searchEmployees(text) resolves the lookup rows; raise(body) resolves {ok, content | errors}
 * (the thunk never rejects). Client checks (employee chosen, required answers, table cells) run
 * first; the server's 422 / 403 messages are listed in the alert block.
 */

export const CHOOSE_EMPLOYEE = "Choose an employee";
export const ANSWER_REQUIRED = "This answer is required";

const ordered = (questions) => (questions || []).slice().sort((a, b) => Number(a.position) - Number(b.position));

const blankValue = (q) => {
  if (q.qtype === "multi") return [];
  if (q.qtype === "table") return [blankRow(tableDef(q).columns)];
  return "";
};

const isBlank = (v) => v === null || v === undefined || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

// What is sent for one answer (SPEC 23.3 / 19.4 canonical values).
export const canonical = (q, value) => {
  switch (q.qtype) {
    case "table": return cleanTable(tableDef(q).columns, value);
    case "rating": return value === "" ? "" : Number(value);
    case "yes_no": return value === "" ? "" : Number(value);
    case "short_text":
    case "long_text": return typeof value === "string" ? value.trim() : value;
    default: return value;
  }
};

export const employeeLabel = (e) => [e.emp_num, e.name].filter(Boolean).join(" — ") +
  (e.department_name || e.country_name ? " (" + [e.department_name, e.country_name].filter(Boolean).join(", ") + ")" : "");

class RaiseDocumentModal extends Component {
  constructor(props) {
    super(props);
    const values = {};
    ordered(props.questions).forEach((q) => { values[String(q.id)] = blankValue(q); });
    this.state = { employee: null, values: values, problems: {}, errors: [], saving: false };
  }

  loadOptions = (text) => {
    if (String(text || "").trim().length < 2) return Promise.resolve([]);
    return Promise.resolve(this.props.searchEmployees(text)).then((rows) =>
      (rows || []).map((e) => ({ value: e.id, label: employeeLabel(e) }))
    );
  };

  setValue = (q, value) => this.setState({ values: { ...this.state.values, [String(q.id)]: value } });

  check = () => {
    const problems = {};
    const errors = [];
    if (!this.state.employee) errors.push(CHOOSE_EMPLOYEE);
    ordered(this.props.questions).forEach((q) => {
      const value = this.state.values[String(q.id)];
      if (q.qtype === "table") {
        const problem = tableProblem(q, value);
        if (problem) problems[q.id] = problem;
      } else if (q.required && isBlank(value)) {
        problems[q.id] = ANSWER_REQUIRED;
      }
    });
    return { problems, errors };
  };

  body = () => {
    const values = [];
    ordered(this.props.questions).forEach((q) => {
      const value = canonical(q, this.state.values[String(q.id)]);
      if (isBlank(value)) return;
      values.push({ question_id: q.id, value: value });
    });
    return { user_id: this.state.employee.value, form_version: this.props.formVersion, values: values };
  };

  onSave = () => {
    if (this.state.saving) return;
    const { problems, errors } = this.check();
    if (errors.length || Object.keys(problems).length) {
      this.setState({ problems, errors });
      return;
    }
    this.setState({ saving: true, problems: {}, errors: [] });
    Promise.resolve(this.props.raise(this.body())).then((result) => {
      if (result && result.ok) {
        this.props.onSaved(result.content);
        return;
      }
      this.setState({ saving: false, errors: (result && result.errors) || ["The document could not be saved. Please try again."] });
    });
  };

  renderInput = (q) => {
    const id = "raise-q-" + q.id;
    const value = this.state.values[String(q.id)];
    const set = (v) => this.setValue(q, v);
    const disabled = this.state.saving;
    switch (q.qtype) {
      case "table": {
        const def = tableDef(q);
        return <TableInput id={id} label={q.label} columns={def.columns} maxRows={def.maxRows} value={value} onChange={set} disabled={disabled} />;
      }
      case "long_text":
        return <textarea className="form-control" id={id} rows={3} maxLength={4000} value={value} disabled={disabled} onChange={(e) => set(e.target.value)} />;
      case "date":
        return <input type="date" className="form-control" id={id} value={value} disabled={disabled} onChange={(e) => set(e.target.value)} />;
      case "rating":
        return (
          <select className="form-control" id={id} value={value} disabled={disabled} onChange={(e) => set(e.target.value)}>
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={String(n)}>{n}</option>)}
          </select>
        );
      case "yes_no":
        return (
          <select className="form-control" id={id} value={value} disabled={disabled} onChange={(e) => set(e.target.value)}>
            <option value="">—</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );
      case "single":
        return (
          <select className="form-control" id={id} value={value} disabled={disabled} onChange={(e) => set(e.target.value)}>
            <option value="">—</option>
            {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case "multi": {
        const picked = Array.isArray(value) ? value : [];
        return (q.options || []).map((o, i) => (
          <div className="form-check" key={o}>
            <input type="checkbox" className="form-check-input" id={id + "-o" + i} checked={picked.includes(o)} disabled={disabled}
              onChange={() => set((q.options || []).filter((x) => (x === o ? !picked.includes(x) : picked.includes(x))))} />
            <label className="form-check-label" htmlFor={id + "-o" + i}>{o}</label>
          </div>
        ));
      }
      default:
        return <input type="text" className="form-control" id={id} maxLength={255} value={value} disabled={disabled} onChange={(e) => set(e.target.value)} />;
    }
  };

  render() {
    const { errors, problems, saving } = this.state;
    const questions = ordered(this.props.questions);
    return (
      <Modal show={true} size="lg" onHide={this.props.onClose} aria-labelledby="raise-document-title" className="broadcast-raise-modal">
        <Modal.Header closeButton>
          <Modal.Title id="raise-document-title">Raise document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.length ? (
            <div role="alert" className="alert alert-danger">
              <ul className="mb-0">{errors.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="raise-employee">Employee</label>
            <AsyncSelect inputId="raise-employee" classNamePrefix="raise-employee" cacheOptions={false} defaultOptions={false}
              loadOptions={this.loadOptions} value={this.state.employee} isDisabled={saving} isClearable
              placeholder="Type an employee number or name"
              noOptionsMessage={({ inputValue }) => (String(inputValue || "").trim().length < 2 ? "Type at least 2 characters" : "No employee found")}
              onChange={(option) => this.setState({ employee: option || null })} />
          </div>
          {questions.map((q) => (
            <div className="form-group broadcast-question" key={q.id}>
              <label htmlFor={q.qtype === "multi" || q.qtype === "table" ? undefined : "raise-q-" + q.id}>{q.label}</label>
              {q.required ? <span className="broadcast-required" title="Required">*</span> : null}
              {this.renderInput(q)}
              {problems[q.id] ? <div className="text-danger broadcast-question-error">{problems[q.id]}</div> : null}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={this.onSave} disabled={saving}>Save</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default RaiseDocumentModal;
